import React, { useState, useEffect } from 'react';
import { Card, Descriptions, Button, Space, Toast, Modal, Form, Table } from '@douyinfe/semi-ui';
import request from '../../utils/request';
import styles from './index.module.scss';
import { useNavigate } from 'react-router-dom';
import { formatBytes } from '../../utils/clipboard';

const Profile = () => {
  const [userInfo, setUserInfo] = useState(JSON.parse(localStorage.getItem('userInfo') || '{}'));
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const formatTimestamp = (timestamp) => {
    if (!timestamp || (timestamp == '0')) return '-';
    const date = new Date(parseInt(timestamp));
    return date.toLocaleString();
  };

  return (
    <div className={styles.profileContainer}>
      <Card
        title="账号信息"
        headerStyle={{ backgroundColor: 'var(--semi-color-bg-2)' }}
        className={styles.profileCard}
      >
        <div className={styles.profileContent}>
          <div className={styles.profileSection}>
            <Descriptions data={[
              { key: '用户ID', value: userInfo.uid || '-' },
              { key: '账号', value: userInfo.account || '-' },
              { key: '用户名', value: userInfo.name || '-' }
            ]} />
          </div>
          <div className={styles.profileSection}>
            <Descriptions data={[
              { key: '版本', value: userInfo.version || '-' },
              { key: '创建时间', value: formatTimestamp(userInfo.create_at) },
              { key: '过期时间', value: formatTimestamp(userInfo.expire_at) }
            ]} />
          </div>
        </div>
        <div className={styles.actionButtons}>
          <Button type="primary" onClick={() => setShowEditModal(true)}>编辑信息</Button>
          <Button type="warning" onClick={() => setShowPasswordModal(true)}>修改密码</Button>
        </div>
      </Card>

      {/* 资产信息面板 */}
      <Card
        title="资产信息"
        headerStyle={{ backgroundColor: 'var(--semi-color-bg-2)' }}
        className={styles.profileCard}
      >
        <AssetPanel />
      </Card>

      <Modal
        title="编辑信息"
        visible={showEditModal}
        onCancel={() => setShowEditModal(false)}
        footer={null}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        bodyStyle={{ width: '100%', maxWidth: '520px' }}
      >
        <Form
          onSubmit={async (values) => {
            setLoading(true);
            try {
              const response = await request.post('/go/spc/edit/', {
                name: values.name
              });
              if (response.errno === 0) {
                const newUserInfo = { ...userInfo, name: values.name };
                setUserInfo(newUserInfo);
                localStorage.setItem('userInfo', JSON.stringify(newUserInfo));
                Toast.success('更新成功');
                setShowEditModal(false);
              } else {
                Toast.error(response.errstr || '更新失败');
              }
            } catch (error) {
              console.error('更新失败:', error);
              Toast.error('更新失败，请重试');
            } finally {
              setLoading(false);
            }
          }}
          initValues={{ name: userInfo.name }}
        >
          <Form.Input
            field="name"
            label="用户名"
            placeholder="请输入用户名（最多16位中英文字符）"
            rules={[{
              required: true,
              message: '请输入用户名',
              validator: (rule, value) => {
                return value && value.length <= 16 && /^[\u4e00-\u9fa5a-zA-Z0-9]+$/.test(value);
              },
              message: '用户名必须为16位以内的中英文字符'
            }]}
          />
          <div style={{ marginTop: 24, textAlign: 'right', paddingBottom: 24 }}>
            <Space>
              <Button type="tertiary" onClick={() => setShowEditModal(false)}>取消</Button>
              <Button type="primary" htmlType="submit" loading={loading}>确定</Button>
            </Space>
          </div>
        </Form>
      </Modal>

      <Modal
        title="修改密码"
        visible={showPasswordModal}
        onCancel={() => setShowPasswordModal(false)}
        footer={null}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        bodyStyle={{ width: '100%', maxWidth: '520px' }}
      >
        <Form
          onSubmit={async (values) => {
            if (values.newPassword !== values.confirmPassword) {
              Toast.error('两次输入的密码不一致');
              return;
            }

            setLoading(true);
            try {
              const response = await request.post('/go/spc/editpasswd/', {
                old: values.oldPassword,
                passwd: values.newPassword
              });
              if (response.errno === 0) {
                Toast.success('密码修改成功');
                setShowPasswordModal(false);
              } else {
                Toast.error(response.errstr || '密码修改失败');
              }
            } catch (error) {
              console.error('密码修改失败:', error);
              Toast.error('密码修改失败，请重试');
            } finally {
              setLoading(false);
            }
          }}
        >
          <Form.Input
            field="oldPassword"
            label="当前密码"
            type="password"
            placeholder="请输入当前密码"
            rules={[{ required: true, message: '请输入当前密码' }]}
          />
          <Form.Input
            field="newPassword"
            label="新密码"
            type="password"
            placeholder="请输入新密码（至少6位）"
            rules={[{
              required: true,
              message: '请输入新密码',
              validator: (rule, value) => {
                return value && value.length >= 6;
              },
              message: '密码长度不能少于6位'
            }]}
          />
          <Form.Input
            field="confirmPassword"
            label="确认密码"
            type="password"
            placeholder="请再次输入新密码"
            rules={[{ required: true, message: '请再次输入新密码' }]}
          />
          <div style={{ marginTop: 24, textAlign: 'right', paddingBottom: 24 }}>
            <Space>
              <Button type="tertiary" onClick={() => setShowPasswordModal(false)}>取消</Button>
              <Button type="primary" htmlType="submit" loading={loading}>确定</Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

const AssetPanel = () => {
  const [loading, setLoading] = useState(false);
  const [assetList, setAssetList] = useState([]);
  const [typeList, setTypeList] = useState([]);
  const navigate = useNavigate();

  // 字节格式化函数
  useEffect(() => {
    fetchAssetList();
  }, []);

  const fetchAssetList = async () => {
    setLoading(true);
    try {
      const res = await request.get('/go/sp/sp/list_service_provider_limit/', {
        params: { page: 0, limit: 100 }
      });
      if (res.errno === 0) {
        setAssetList(res.list || []);
      } else {
        Toast.error(res.errstr || '获取资产信息失败');
      }
    } catch (e) {
      Toast.error('获取资产信息失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Table
      loading={loading}
      columns={[
        { title: '类型', dataIndex: 'name' },
        {
          title: '剩余',
          dataIndex: 't_remain',
          render: (text, record) => (
            <a
              style={{ color: '#2d5af1', cursor: 'pointer' }}
              onClick={() => navigate(`/accountassets?typ=${record.typ}`)}
            >
              {record.typ === 4 ? formatBytes(text) : text}
            </a>
          )
        },
        {
          title: '已使用',
          dataIndex: 't_used',
          render: (text, record) => (
            record.typ === 4 ? formatBytes(text) : text
          )
        },
      ]}
      dataSource={assetList}
      rowKey={r => r.typ}
      pagination={false}
      style={{ marginTop: 8 }}
    />
  );
};

export default Profile;